import { SettlementStatus } from '@dill/dill-shared';
import { CheckCircle, Clock, Question, XCircle } from 'phosphor-react';

/**
 * Whether the bank has confirmed the money, which is a different question from how the
 * payment is applied. A record can be confidently matched to invoices and still be waiting
 * for the deposit — or settled and still need a human to check the allocation.
 */
const settlementConfig: Record<
  SettlementStatus,
  { label: string; icon: React.ReactNode; className: string }
> = {
  SETTLED: {
    label: 'Settled',
    icon: <CheckCircle size={12} weight="fill" />,
    className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  },
  PENDING: {
    label: 'Awaiting settlement',
    icon: <Clock size={12} weight="bold" />,
    className: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  },
  AMBIGUOUS: {
    label: 'Needs review',
    icon: <Question size={12} weight="bold" />,
    className: 'bg-orange-50 text-orange-700 ring-orange-600/20',
  },
  FAILED: {
    label: 'Failed',
    icon: <XCircle size={12} weight="fill" />,
    className: 'bg-red-50 text-red-700 ring-red-600/20',
  },
  // No bank feed linked, so there is nothing to confirm against. Muted rather than alarming:
  // this is the normal state for a supplier who has not connected a bank.
  NOT_APPLICABLE: {
    label: 'No bank feed',
    icon: null,
    className: 'bg-gray-50 text-gray-500 ring-gray-400/20',
  },
};

interface SettlementBadgeProps {
  status: SettlementStatus;
}

/**
 * How late a record is deliberately does not appear here. Lateness is derived from the advice
 * date rather than stored, and the queue that cares already shows it as a day count — saying it
 * twice in one row reads as two different problems.
 */
export const SettlementBadge = ({ status }: SettlementBadgeProps) => {
  const config = settlementConfig[status] ?? settlementConfig.NOT_APPLICABLE;

  return (
    <span
      className={
        // whitespace-nowrap as well as a wide enough column: the longest label, "Awaiting
        // settlement", wraps into two lines and doubles the row height as soon as the table is
        // compressed, which a fixed width alone does not prevent.
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset whitespace-nowrap ' +
        config.className
      }>
      {config.icon}
      {config.label}
    </span>
  );
};
